import { parsePositiveInteger } from '@cascade/core/utils';
import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { NavigateOptions } from 'react-router-dom';
import type {
  TPaginationParams,
  TPaginationParamsOptions,
} from './usePaginationParams.types.ts';

const PAGE_PARAM = 'page';
const PAGE_SIZE_PARAM = 'page_size';
const FIRST_PAGE = 1;

export const usePaginationParams = ({
  defaultPageSize,
  pageCount,
}: TPaginationParamsOptions = {}): TPaginationParams => {
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = searchParams.get(PAGE_PARAM);
  const pageSizeParam = searchParams.get(PAGE_SIZE_PARAM);
  const page = parsePositiveInteger(pageParam) ?? FIRST_PAGE;
  const pageSize = parsePositiveInteger(pageSizeParam) ?? defaultPageSize;
  const lastPage = Math.max(pageCount ?? page, FIRST_PAGE);
  const isPastLastPage = page > lastPage;

  const writeParams = useCallback(
    (values: Record<string, number>, options?: NavigateOptions) =>
      setSearchParams((params) => {
        Object.entries(values).forEach(([name, value]) => {
          const text = String(value);

          params.set(name, text);
        });

        return params;
      }, options),
    [setSearchParams],
  );

  const setPage = useCallback(
    (nextPage: number) => {
      if (nextPage === page) {
        return;
      }

      writeParams({ [PAGE_PARAM]: nextPage });
    },
    [page, writeParams],
  );

  const setPageSize = useCallback(
    (nextPageSize: number) => {
      if (nextPageSize === pageSize) {
        return;
      }

      writeParams({
        [PAGE_PARAM]: FIRST_PAGE,
        [PAGE_SIZE_PARAM]: nextPageSize,
      });
    },
    [pageSize, writeParams],
  );

  useEffect(() => {
    if (!isPastLastPage) {
      return;
    }

    writeParams({ [PAGE_PARAM]: lastPage }, { replace: true });
  }, [isPastLastPage, lastPage, writeParams]);

  return useMemo(
    () => ({ page, pageSize, isPastLastPage, setPage, setPageSize }),
    [page, pageSize, isPastLastPage, setPage, setPageSize],
  );
};
